/**
 * CuratorEventHub — главная страница куратора по конкретному мероприятию.
 * Показывает оперативный статус: онбординг экспонентов, аккредитация,
 * активность дня, задачи и быстрые действия.
 */
import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import {
  Building2, Users, QrCode, BarChart3, Map, CalendarCheck,
  CheckCircle2, AlertCircle, Clock, ChevronRight, Bell,
  Send, UserCheck, Download, RefreshCw, Loader2,
  CalendarDays, MapPin, Activity,
} from "lucide-react";
import type { CompanySummary } from "@mvk/shared";
import { useAdminEvent } from "../../hooks/useAdminEvent";
import { tryLoadExhibitors } from "../../../services/adminEventsApi";
import { catalogService } from "../../../services/catalogService";
import type { Company } from "../../../types";
import {
  AnnouncementLaunchButton,
  AnnouncementModal,
} from "../../components/AnnouncementModal";

function summaryToCompany(summary: CompanySummary, eventId: string): Company {
  return {
    id: summary.id,
    eventId,
    name: summary.name,
    category: summary.category,
    tags: [],
    hall: summary.hall ?? "",
    booth: summary.booth ?? "",
    shortAbout: summary.shortAbout,
    products: [],
    contact: {},
    qrCodeValue: `company:${summary.id}`,
    mapPoint: { x: 0, y: 0, hallId: "" },
  };
}

function getExhibitorStatus(company: Company, repCount: number) {
  const profileComplete = !!(company.shortAbout && company.contact.phone && company.contact.email);
  const qrReady = !!company.qrCodeValue;
  const hasReps = repCount > 0;
  if (profileComplete && qrReady && hasReps) return "ready";
  if (profileComplete && qrReady) return "no_reps";
  if (profileComplete) return "no_qr";
  return "incomplete";
}

const STATUS_CONFIG = {
  ready:       { label: "Готов",             color: "text-emerald-600", bg: "bg-emerald-50", dot: "bg-emerald-500" },
  no_reps:     { label: "Нет представителей", color: "text-amber-600",  bg: "bg-amber-50",  dot: "bg-amber-400"  },
  no_qr:       { label: "Нет QR",            color: "text-orange-600",  bg: "bg-orange-50", dot: "bg-orange-400" },
  incomplete:  { label: "Профиль не заполнен", color: "text-red-600",   bg: "bg-red-50",    dot: "bg-red-400"    },
};

function daysUntil(iso: string) {
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
}

// ─── Main component ────────────────────────────────────────────────────────
export function CuratorEventHub() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const { event, loading: eventLoading } = useAdminEvent(id);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [repCounts, setRepCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await tryLoadExhibitors(id);
        const mapped = (list ?? []).map((s) => summaryToCompany(s, id));
        if (cancelled) return;
        setCompanies(mapped);
        const entries = await Promise.all(
          mapped.map(async (c) => {
            try {
              const reps = await catalogService.listRepresentatives(id, c.id, "admin");
              return [c.id, reps.length] as const;
            } catch {
              return [c.id, 0] as const;
            }
          }),
        );
        if (!cancelled) setRepCounts(Object.fromEntries(entries));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const statuses = useMemo(
    () =>
      companies.map((c) => ({
        company: c,
        status: getExhibitorStatus(c, repCounts[c.id] ?? 0),
      })),
    [companies, repCounts],
  );

  if (eventLoading || loading) {
    return (
      <div className="p-6 flex justify-center text-gray-400">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  if (!event) return <div className="p-6 text-gray-500">Мероприятие не найдено</div>;

  // Derived values (not hooks — safe after guard)
  const readyCount    = statuses.filter((s) => s.status === "ready").length;
  const noRepsCount   = statuses.filter((s) => s.status === "no_reps").length;
  const incompleteCount = statuses.filter((s) => s.status !== "ready").length;
  const completionPct = companies.length > 0 ? Math.round((readyCount / companies.length) * 100) : 0;

  const days = daysUntil(event.startDate);
  const isActive = event.status === "active";
  const isUpcoming = event.status === "upcoming";
  const isPast = event.status === "past";

  // Pending tasks
  const tasks = [
    incompleteCount > 0 && {
      id: "incomplete",
      text: `${incompleteCount} экспонентов не завершили онбординг`,
      severity: "warn" as const,
      action: () => navigate(`/events/${id}/exhibitors`),
    },
    noRepsCount > 0 && {
      id: "no_reps",
      text: `${noRepsCount} компаний без представителей (без бейджей)`,
      severity: "warn" as const,
      action: () => navigate(`/events/${id}/exhibitors`),
    },
    !isPast && {
      id: "qr",
      text: "Распечатайте QR-коды для всех стендов",
      severity: "info" as const,
      action: () => navigate(`/events/${id}/exhibitors`),
    },
  ].filter(Boolean) as { id: string; text: string; severity: "warn" | "info"; action: () => void }[];

  const quickLinks = [
    { label: "Экспоненты",     icon: <Building2 size={18} />,    href: `/events/${id}/exhibitors`, badge: incompleteCount > 0 ? incompleteCount : undefined, badgeColor: "bg-red-500" },
    { label: "Аккредитация",   icon: <UserCheck size={18} />,    href: `/events/${id}/accreditation` },
    { label: "Карта / стенды", icon: <Map size={18} />,          href: `/events/${id}/map` },
    { label: "Программа",      icon: <CalendarCheck size={18} />, href: `/events/${id}/schedule` },
    { label: "Аналитика",      icon: <BarChart3 size={18} />,    href: `/events/${id}/stats` },
  ];

  const activityFeed = [
    { text: "GOLDTEX завершил заполнение профиля", time: "5 мин", icon: <CheckCircle2 size={13} className="text-emerald-500" /> },
    { text: "BAIZHENG добавил 2 представителей", time: "18 мин", icon: <Users size={13} className="text-indigo-400" /> },
    { text: "Новый экспонент: ORIGANO зарегистрирован", time: "1 ч",  icon: <Building2 size={13} className="text-violet-400" /> },
    { text: "QR-код стенда A-309 сгенерирован",  time: "2 ч",  icon: <QrCode size={13} className="text-gray-400" /> },
    { text: "STEPP изменил контактный телефон",  time: "3 ч",  icon: <Bell size={13} className="text-amber-400" /> },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-5">
      {showAnnouncement && id ? (
        <AnnouncementModal eventId={id} onClose={() => setShowAnnouncement(false)} />
      ) : null}

      {/* ── Event header ───────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="relative h-32 sm:h-44">
          <img src={event.heroImage} alt={event.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
            <div className="flex items-end justify-between gap-3">
              <div>
                <h1 className="text-[20px] sm:text-[22px] leading-tight">{event.title}</h1>
                <div className="flex items-center gap-2 text-white/80 text-[12px] mt-1">
                  <MapPin size={11} /> {event.city} · {event.venueName}
                </div>
              </div>
              {/* Status pill */}
              {isActive && (
                <div className="flex items-center gap-1.5 bg-emerald-500 text-white text-[12px] px-3 py-1 rounded-full flex-shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  Идёт сейчас
                </div>
              )}
              {isUpcoming && (
                <div className="flex flex-col items-end bg-white/20 backdrop-blur-sm rounded-xl px-3 py-2 flex-shrink-0 text-center">
                  <span className="text-[22px] leading-none">{Math.max(0, days)}</span>
                  <span className="text-[10px] text-white/80">{days === 1 ? "день" : days < 5 ? "дня" : "дней"}</span>
                </div>
              )}
              {isPast && (
                <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm text-white text-[12px] px-3 py-1 rounded-full flex-shrink-0">
                  Завершено
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dates strip */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-50 text-[12px] text-gray-500">
          <span className="flex items-center gap-1.5">
            <CalendarDays size={13} className="text-gray-400" />
            {formatDate(event.startDate)} — {formatDate(event.endDate)}
          </span>
          <span className="text-gray-400">{event.industry}</span>
        </div>
      </div>

      {/* ── Onboarding progress ────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-[14px] text-gray-900">Готовность экспонентов</h2>
            <p className="text-[12px] text-gray-400 mt-0.5">{readyCount} из {companies.length} полностью готовы</p>
          </div>
          <div className="text-[28px] text-gray-900 leading-none">{completionPct}%</div>
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-gray-100 rounded-full mb-4 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${completionPct === 100 ? "bg-emerald-500" : completionPct > 60 ? "bg-indigo-500" : "bg-amber-400"}`}
            style={{ width: `${completionPct}%` }}
          />
        </div>

        {/* Status breakdown */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {(Object.entries(STATUS_CONFIG) as [keyof typeof STATUS_CONFIG, typeof STATUS_CONFIG[keyof typeof STATUS_CONFIG]][]).map(([key, cfg]) => {
            const count = statuses.filter((s) => s.status === key).length;
            return (
              <div key={key} className={`rounded-xl p-3 ${cfg.bg}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                  <span className={`text-[10px] uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>
                </div>
                <div className={`text-[22px] leading-none ${cfg.color}`}>{count}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {/* ── Quick links ──────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-4 pt-4 pb-2">
            <h2 className="text-[13px] text-gray-700">Разделы</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {quickLinks.map((l) => (
              <button key={l.href} onClick={() => navigate(l.href)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-500 flex-shrink-0">
                  {l.icon}
                </div>
                <span className="flex-1 text-[13px] text-gray-800">{l.label}</span>
                {l.badge != null && (
                  <span className={`w-5 h-5 rounded-full ${l.badgeColor} text-white text-[10px] flex items-center justify-center flex-shrink-0`}>
                    {l.badge}
                  </span>
                )}
                <ChevronRight size={14} className="text-gray-300 flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>

        {/* ── Tasks / alerts ───────────────────────────────────────────── */}
        <div className="space-y-3">
          {/* Pending tasks */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h2 className="text-[13px] text-gray-700 mb-3">Требует внимания</h2>
            {tasks.length === 0 ? (
              <div className="flex items-center gap-2 text-emerald-600 text-[13px]">
                <CheckCircle2 size={16} /> Всё готово, нет открытых задач
              </div>
            ) : (
              <div className="space-y-2">
                {tasks.map((t) => (
                  <button key={t.id} onClick={t.action}
                    className={`w-full flex items-start gap-2.5 p-2.5 rounded-xl text-left transition-colors ${
                      t.severity === "warn" ? "bg-amber-50 hover:bg-amber-100" : "bg-indigo-50 hover:bg-indigo-100"
                    }`}>
                    {t.severity === "warn"
                      ? <AlertCircle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                      : <Clock size={14} className="text-indigo-400 mt-0.5 flex-shrink-0" />
                    }
                    <span className={`text-[12px] ${t.severity === "warn" ? "text-amber-700" : "text-indigo-700"}`}>
                      {t.text}
                    </span>
                    <ChevronRight size={12} className="ml-auto mt-0.5 text-gray-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quick actions */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h2 className="text-[13px] text-gray-700 mb-3">Быстрые действия</h2>
            <div className="space-y-2">
              <AnnouncementLaunchButton onClick={() => setShowAnnouncement(true)} />
              <button onClick={() => navigate(`/events/${id}/exhibitors`)}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl bg-gray-50 hover:bg-gray-100 text-gray-700 transition-colors text-left">
                <Download size={14} className="text-gray-500" />
                <span className="text-[13px]">Скачать все QR-коды</span>
              </button>
              <button onClick={() => navigate(`/events/${id}/accreditation`)}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl bg-gray-50 hover:bg-gray-100 text-gray-700 transition-colors text-left">
                <UserCheck size={14} className="text-emerald-500" />
                <span className="text-[13px]">Аккредитация участников</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Live stats (if active) ─────────────────────────────────────── */}
      {(isActive || isPast) && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[13px] text-gray-700">
              {isActive ? "Статистика в реальном времени" : "Итоговая статистика"}
            </h2>
            {isActive && (
              <button className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-indigo-600 transition-colors">
                <RefreshCw size={12} /> Обновить
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Посетителей сегодня", value: isActive ? "1 240" : event.visitorsCount.toLocaleString("ru-RU"), icon: <Users size={16} className="text-sky-500" />, bg: "bg-sky-50" },
              { label: "Сканирований QR", value: isActive ? "487" : "4 750", icon: <QrCode size={16} className="text-indigo-500" />, bg: "bg-indigo-50" },
              { label: "Активных стендов", value: companies.length, icon: <Activity size={16} className="text-emerald-500" />, bg: "bg-emerald-50" },
              { label: "Чатов открыто", value: isActive ? "23" : "312", icon: <Send size={16} className="text-violet-500" />, bg: "bg-violet-50" },
            ].map((s) => (
              <div key={s.label} className="bg-gray-50 rounded-xl p-3">
                <div className={`w-7 h-7 rounded-lg ${s.bg} flex items-center justify-center mb-2`}>{s.icon}</div>
                <div className="text-[20px] text-gray-900 leading-none">{s.value}</div>
                <div className="text-[11px] text-gray-500 mt-1 leading-snug">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Activity feed ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <h2 className="text-[13px] text-gray-700 mb-3">Последние действия экспонентов</h2>
        <div className="space-y-3">
          {activityFeed.map((item, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <div className="w-6 h-6 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                {item.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-gray-800">{item.text}</div>
              </div>
              <div className="text-[11px] text-gray-400 flex-shrink-0">{item.time} назад</div>
            </div>
          ))}
        </div>
        <button onClick={() => navigate(`/events/${id}/stats`)}
          className="mt-3 w-full py-2 rounded-lg border border-dashed border-gray-200 text-[12px] text-gray-400 hover:border-indigo-200 hover:text-indigo-500 transition-colors">
          Подробная аналитика →
        </button>
      </div>
    </div>
  );
}
